import { AUTHORIZATION_TOKEN_EVENTS } from 'ts/common/constants/auth';

const DIGITAL_RULE_EVENTS = {
    LIST: 'client-gallery-digital-rule-list',
    LIST_ERROR: 'client-gallery-digital-rule-list-error',
    LISTED: 'client-gallery-digital-rule-listed'
};
const EVENT_ALBUM_EVENTS = {
    GET: 'client-gallery-event-album-get',
    GOT: 'client-gallery-event-album-got'
};
const EVENT_EVENTS = {
    GET: 'client-gallery-event-get',
    GOT: 'client-gallery-event-got'
};
const EVENT_TAG_EVENTS = {
    GET: 'client-gallery-event-tags-get',
    GOT: 'client-gallery-event-tags-got'
};
const GOOGLE_PHOTOS_EXPORT_EVENTS = {
    GET: 'client-gallery-google-photos-export-get',
    GOT: 'client-gallery-google-photos-export-got',
    ERRORS: {
        UNAUTHORIZED: 'client-gallery-google-photos-export-errors-unauthorized',
        UNKNOWN: 'client-gallery-google-photos-export-errors-unknown'
    }
};
const MUSIC_EVENTS = {
    // Emitted by a slideshow while its own music is claiming exclusive playback (playing and has
    // song segments), so gallery background music (spMusic) ducks out instead of playing over it.
    // UNMUTE only clears the "slideshow is using audio" bookkeeping — it never resumes gallery
    // music, since only *starting* (never resuming) the other side should affect it.
    MUTE: 'client-gallery-music-mute',
    UNMUTE: 'client-gallery-music-unmute',
    // Emitted only by the lightbox slideshow viewer, carrying whether its own song is sounding.
    // Distinct from MUTE/UNMUTE — which the intro page's cover slideshow emits too — because both
    // things spMusic does with it must happen for the lightbox and never for the intro page:
    //
    //  - true: hide the music control. It governs the gallery playlist and the intro cover's song,
    //    but *not* the lightbox player, which owns its audio outright (see ClientSlideshowViewer).
    //    Leaving it on screen would advertise control it doesn't have. The intro page is the
    //    opposite case — there the control exists precisely to govern the cover's song.
    //  - false: show the control again and resume the gallery playlist from where ducking it
    //    paused it — the one deliberate exception to "only starting one side affects the other,
    //    never resuming it". On the intro page the playlist is deferred and must not start at all
    //    (see `musicDeferred` and spMusic's route-change handoff), which is why UNMUTE can't carry
    //    this.
    LIGHTBOX_SLIDESHOW_AUDIO: 'client-gallery-music-lightbox-slideshow-audio',
    // Mirror image of MUTE: emitted by gallery background music (spMusic) when it (re)starts, so
    // an open slideshow pauses instead of playing over it. There's no GALLERY_PAUSED counterpart
    // to UNMUTE — pausing one side never resumes the other, only starting one pauses the other.
    // (LIGHTBOX_SLIDESHOW_AUDIO above is the one narrow exception, in the lightbox's direction.)
    GALLERY_PLAYING: 'client-gallery-music-gallery-playing',
    // Emitted on every lightbox photo navigation (arrows, keyboard, swipe, filmstrip thumbnail
    // click), so any slideshow left mounted off-screen stops playing instead of continuing
    // silently in the background — the lightbox's filmstrip keeps a window of nearby photos
    // mounted for animation, so navigating to an adjacent photo does not by itself unmount it.
    STOP_SLIDESHOW: 'client-gallery-music-stop-slideshow',
    // Emitted by gallery background music (spMusic) whenever its volume level changes — both on
    // user interaction with its volume slider and, to sync a just-activated slideshow regardless
    // of Angular/React mount order, at the moment a slideshow takes over audio (MUTE). Carries
    // the numeric volume level (0..1) so any listening slideshow audio can be kept in sync with
    // the one shared volume control.
    VOLUME: 'client-gallery-music-volume',
    // Emitted by gallery background music (spMusic) whenever the shared mute state changes —
    // on the visitor's own mute/unmute click and, like VOLUME, at the moment a slideshow takes
    // over audio (MUTE). Carries the boolean muted state so a slideshow serving as the page's
    // audio silences (and un-silences) its own song *without stopping it*, keeping that song on
    // the slideshow's timeline instead of restarting it.
    MUTED: 'client-gallery-music-muted'
};

/**
 * How long a slideshow cover's own song takes to fade out when the visitor leaves the intro page
 * for the gallery, so the handoff to the gallery playlist is a fade rather than a hard cut.
 *
 * Shared by both halves of that handoff: the slideshow's audio ramps its volume down over this
 * long (see `useSlideshowAudio`), and gallery background music (spMusic) waits this long before
 * starting the playlist, so the two never sound at once.
 */
const SLIDESHOW_AUDIO_FADE_OUT_MS = 450;

const PACKAGE_EVENTS = {
    SELECT: 'client-gallery-package-select',
    PHOTO_ASSIGN: 'client-gallery-package-photo-assign',
    PHOTO_LOCATION_RESET: 'client-gallery-package-photo-location-reset'
};
const PHOTO_DOWNLOAD_EVENTS = {
    CREATE: 'client-gallery-photo-download-create',
    CREATED: 'client-gallery-photo-download-created',
    ERRORS: {
        ALBUM: 'client-gallery-photo-download-errors-album',
        PIN: 'client-gallery-photo-download-errors-pin',
        TOKEN_INVALID: 'client-gallery-photo-download-errors-token-invalid',
        TIMEOUT: 'client-gallery-photo-download-errors-timeout'
    }
};
const PRICE_SHEET_ITEM_EVENTS = {
    LIST: 'client-gallery-price-sheet-item-list',
    LISTED: 'client-gallery-price-sheet-item-listed'
};
const VIDEO_DOWNLOAD_EVENTS = {
    CREATE: 'client-gallery-video-download-create',
    CREATED: 'client-gallery-video-download-created',
    ERRORS: {
        ALBUM: 'client-gallery-video-download-errors-album',
        PIN: 'client-gallery-video-download-errors-pin',
        TOKEN_INVALID: 'client-gallery-video-download-errors-token-invalid',
        TIMEOUT: 'client-gallery-video-download-errors-timeout'
    }
};
const VIDEO_EVENTS = {
    STOP: 'client-gallery-video-stop'
};
const ZIP_BUNDLE_EVENTS = {
    CREATE: 'client-gallery-zip-bundle-create',
    CREATED: 'client-gallery-zip-bundle-created',
    GET: 'client-gallery-zip-bundle-get',
    GOT: 'client-gallery-zip-bundle-got',
    ERRORS: {
        ALBUM: 'client-gallery-zip-bundle-errors-album',
        PIN: 'client-gallery-zip-bundle-errors-pin',
        TOKEN_INVALID: 'client-gallery-zip-bundle-errors-token-invalid',
        ALL_MEDIA_LIMITED: 'client-gallery-zip-bundle-errors-all-media-limited'
    }
};

export {
    AUTHORIZATION_TOKEN_EVENTS,
    DIGITAL_RULE_EVENTS,
    EVENT_ALBUM_EVENTS,
    EVENT_EVENTS,
    EVENT_TAG_EVENTS,
    GOOGLE_PHOTOS_EXPORT_EVENTS,
    MUSIC_EVENTS,
    PACKAGE_EVENTS,
    SLIDESHOW_AUDIO_FADE_OUT_MS,
    PHOTO_DOWNLOAD_EVENTS,
    PRICE_SHEET_ITEM_EVENTS,
    VIDEO_DOWNLOAD_EVENTS,
    VIDEO_EVENTS,
    ZIP_BUNDLE_EVENTS
};

export default {
    AUTHORIZATION_TOKEN_EVENTS,
    DIGITAL_RULE_EVENTS,
    EVENT_ALBUM_EVENTS,
    EVENT_EVENTS,
    EVENT_TAG_EVENTS,
    GOOGLE_PHOTOS_EXPORT_EVENTS,
    MUSIC_EVENTS,
    PACKAGE_EVENTS,
    SLIDESHOW_AUDIO_FADE_OUT_MS,
    PHOTO_DOWNLOAD_EVENTS,
    PRICE_SHEET_ITEM_EVENTS,
    VIDEO_DOWNLOAD_EVENTS,
    VIDEO_EVENTS,
    ZIP_BUNDLE_EVENTS
};
